import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import KpiCard from '@app/components/KpiCard';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useCursorPagination } from '@app/hooks/useCursorPagination';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { formatDateTime, formatNumber } from '@app/utils/format';
import { resolveBasePath } from '@app/utils/api';
import { buildOrgScopedPath } from '@app/utils/org-routes';
import { buildUnifiedIncidentHeadline, formatExternalTicketLabel, safeExternalTicketHref } from '@app/utils/signals';
import { useConfig } from '@config/provider';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import type { UnifiedIncidentExternalTicket, UnifiedIncidentListItem, UnifiedIncidentListResponse } from './incidents/unifiedTypes';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

type IncidentSummarySnapshot = {
  incidentTotalCount: number;
  withItsmState: number;
  assigned: number;
  withConfidence: number;
};

const UnifiedIncidentsPage = () => {
  const orgId = useActiveOrgId();
  const navigate = useNavigate();
  const location = useLocation();
  const { apiBaseUrl } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchOrgResource } = useAuthFetch();
  const [refreshTick, setRefreshTick] = useState(0);
  const manualRefreshPendingRef = useRef(false);
  const [summarySnapshot, setSummarySnapshot] = useState<IncidentSummarySnapshot | null>(null);
  const {
    state: { page, pageSize, cursor },
    setPage,
    setPageSize,
    reset,
    registerPageMeta,
    deriveMeta
  } = useCursorPagination({ initialPageSize: PAGE_SIZE_OPTIONS[0], pageSizeOptions: PAGE_SIZE_OPTIONS });

  const incidentsFetcher = useMemo(() => {
    if (!orgId) {
      return null;
    }
    return () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      if (cursor.direction === 'after' && cursor.value) {
        params.set('after', cursor.value);
      }
      return fetchOrgResource<UnifiedIncidentListResponse>(basePath, orgId, `incidents?${params.toString()}`);
    };
  }, [basePath, cursor.direction, cursor.value, fetchOrgResource, orgId, pageSize, refreshTick]);

  const { data, loading, error } = useApiQuery<UnifiedIncidentListResponse>(incidentsFetcher, [incidentsFetcher]);
  const incidents = data?.items ?? [];
  const initialLoading = loading && !data;

  useEffect(() => {
    registerPageMeta(data?.meta ?? null);
  }, [data?.meta, registerPageMeta]);

  useEffect(() => {
    reset();
  }, [orgId, reset]);

  const paginationMeta = useMemo(() => deriveMeta(incidents.length), [deriveMeta, incidents.length]);
  const incidentTotalCount = paginationMeta.exactTotal ?? paginationMeta.totalCount;
  const currentSummarySnapshot = useMemo<IncidentSummarySnapshot>(
    () => ({
      incidentTotalCount,
      withItsmState: incidents.filter((item: UnifiedIncidentListItem) => hasDisplayValue(item.itsmStateNormalized)).length,
      assigned: incidents.filter((item: UnifiedIncidentListItem) => hasDisplayValue(item.assigneeUser)).length,
      withConfidence: incidents.filter((item: UnifiedIncidentListItem) => hasDisplayValue(item.reapConfidence)).length
    }),
    [incidentTotalCount, incidents]
  );

  useEffect(() => {
    if (!data) {
      return;
    }
    if (manualRefreshPendingRef.current) {
      manualRefreshPendingRef.current = false;
    }
    setSummarySnapshot(currentSummarySnapshot);
  }, [currentSummarySnapshot, data]);

  useEffect(() => {
    manualRefreshPendingRef.current = false;
    setSummarySnapshot(null);
  }, [orgId]);

  const displayedSummary = summarySnapshot ?? currentSummarySnapshot;

  const columns = useMemo<Array<ListWorkspaceColumn<UnifiedIncidentListItem>>>(
    () => [
      {
        key: 'displayId',
        header: 'Incident ID',
        width: '10rem',
        render: (row) => <span className="unified-incidents__cell-strong">{row.displayId || '—'}</span>
      },
      {
        key: 'title',
        header: 'Title',
        width: '22rem',
        render: (row) => (
          <div className="unified-incidents__title-cell">
            <span className="unified-incidents__cell-strong">{buildUnifiedIncidentHeadline(row)}</span>
            {row.ciOrAffectedService ? <span className="unified-incidents__cell-subtle">{row.ciOrAffectedService}</span> : null}
          </div>
        )
      },
      {
        key: 'itsmStateNormalized',
        header: 'Status',
        width: '8rem',
        render: (row) => <span className={tokenBadgeClass()}>{normalizeEmpty(resolveIncidentStatus(row))}</span>
      },
      {
        key: 'priorityNormalized',
        header: 'Priority',
        width: '7rem',
        render: (row) => <span className={tokenBadgeClass()}>{normalizeEmpty(row.priorityNormalized)}</span>
      },
      {
        key: 'assignee',
        header: 'Assignee',
        width: '12rem',
        render: (row) => (
          <div className="unified-incidents__title-cell">
            <span>{row.assigneeUser || 'Unassigned'}</span>
            {row.assigneeTeam ? <span className="unified-incidents__cell-subtle">{row.assigneeTeam}</span> : null}
          </div>
        )
      },
      {
        key: 'externalTickets',
        header: 'External Ticket',
        width: '12rem',
        render: (row) => (row.externalTickets && row.externalTickets.length > 0 ? <UnifiedExternalTicketLinks tickets={row.externalTickets} /> : '—')
      },
      {
        key: 'updatedAt',
        header: 'Updated',
        width: '11rem',
        render: (row) => formatDateTime(row.updatedAt)
      }
    ],
    []
  );

  if (!orgId) {
    return (
      <OrgModuleLayout title="Incidents" description="Canonical incident queue across REAP and external ticketing sources.">
        <OrgModulePlaceholder intent="Select an organization to load incidents." />
      </OrgModuleLayout>
    );
  }

  const topSlot = (
    <div className="kpi-grid">
      <KpiCard label="Incidents" value={initialLoading ? '…' : formatNumber(displayedSummary.incidentTotalCount)} />
      <KpiCard
        label="With ITSM State"
        value={initialLoading ? '…' : formatNumber(displayedSummary.withItsmState)}
      />
      <KpiCard label="Assigned" value={initialLoading ? '…' : formatNumber(displayedSummary.assigned)} />
      <KpiCard
        label="With Confidence"
        value={initialLoading ? '…' : formatNumber(displayedSummary.withConfidence)}
      />
    </div>
  );

  return (
    <OrgModuleLayout
      title="Incidents"
      description="Canonical incident queue across REAP-native incidents and externally sourced tickets."
      topSlot={topSlot}
    >
      <ListWorkspace
        rows={incidents}
        columns={columns}
        rowId={(row) => row.id}
        page={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        paginationMeta={paginationMeta}
        totalCount={paginationMeta.totalCount}
        loading={loading}
        emptyState={<p>No incidents to display.</p>}
        headerLabel="Incident queue"
        headerStatus={error ? <div className="list-workspace__status">Failed to load incidents: {error.message}</div> : undefined}
        selectionDisabled
        onRefresh={() => {
          manualRefreshPendingRef.current = true;
          setRefreshTick((value) => value + 1);
        }}
        onRowClick={(row) => {
          navigate(buildOrgScopedPath(`/app/incidents/${encodeURIComponent(row.id)}`, orgId), {
            state: { from: `${location.pathname}${location.search}` }
          });
        }}
      />
    </OrgModuleLayout>
  );
};

const normalizeEmpty = (value?: string) => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : '—';
};

const hasDisplayValue = (value?: string) => {
  return (value ?? '').trim().length > 0;
};

const tokenBadgeClass = (): string => {
  return 'ui-badge ui-badge--ghost';
};

const resolveIncidentStatus = (incident: UnifiedIncidentListItem): string => {
  const itsmState = (incident.itsmStateNormalized ?? '').trim();
  if (itsmState.length > 0) {
    return itsmState;
  }

  return (incident.reapStatus ?? '').trim();
};

export default UnifiedIncidentsPage;

const UnifiedExternalTicketLinks = ({ tickets }: { tickets: NonNullable<UnifiedIncidentListItem['externalTickets']> }) => {
  const items = tickets
    .map((ticket: UnifiedIncidentExternalTicket) => {
      const label = formatExternalTicketLabel(ticket);
      const href = safeExternalTicketHref(ticket);
      return { label, href };
    })
    .filter((ticket: { label: string; href?: string | null }) => ticket.label.trim().length > 0);

  if (items.length === 0) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
      {items.map((ticket: { label: string; href?: string | null }, index: number) =>
        ticket.href ? (
          <a
            key={`${ticket.label}-${index}`}
            className="ui-badge ui-badge--ghost"
            href={ticket.href}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}>
            {ticket.label}
          </a>
        ) : (
          <span key={`${ticket.label}-${index}`} className="ui-badge ui-badge--ghost">
            {ticket.label}
          </span>
        )
      )}
    </div>
  );
};
